import React, { useId, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconAiDocument } from '../../../../shared/icon/icon'
import { agoText, utcText } from '../../../../shared/date/local-time'
import { listPostmortems } from './ai-tools-api'
import { POSTMORTEM_SOFT_LIMIT_NOTE, SOURCE_FILTERS, listFilters, marksCell, personOrgCell, postmortemRow, receivedWindow } from './ai-tools-words'
import { CLIENT_SPEC, CONSUMER_SPEC, ClientAppFilter, ConsumerStatusFilters, FilterSelect, LOGIN_SPEC, ORGANIZATION_SPEC, OrganizationFilter, PersonChip, RECEIVED_SPEC, ReceivedFilter, STATUS_SPEC, choiceSpec, useClientApps, useListFilters } from './ai-tools-filters'
import { MarkableList, useMarkableList } from './markable-list'
import { useMatchingActions } from './matching-actions'

// The Postmortems tab (contract §4.6, §8; R2): every postmortem the AI tools
// wrote, newest first, filtered by organization, a consumer's marks (a
// status) and a person, and searched in the grid's box. Its title opens the
// postmortem's page (its findings and body); the Person column names who it
// came from. Mark…, Delete and select mode are markable-list.js (shared with
// the other tabs); this holds what is the postmortems': filters, columns,
// links and the grid's own queryKey.
//
// consumers, onMarked, onNeedConsumers: as useMarkableList.

const QUERY_KEY = 'postmortems'
const FAILED = "The postmortems couldn't be loaded."
const markLabel = row => `postmortem "${row.titleText}"`
const pageOf = row => `/admin/ai-tools/postmortems/${row.id}`

const titleCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="ak-name-cell">
      <Link className="fw-semibold text-truncate" to={pageOf(row)} onClick={e => e.stopPropagation()}>{row.titleText}</Link>
      {/* the client app and source, one line so the list fits a laptop's
          window (owner 2026-10-29); the session too on hover */}
      <span className="sw-cell-sub text-truncate" title={[row.clientApp, row.sourceText, `session ${row.sessionText}`].filter(Boolean).join(' · ')}>
        {[row.clientApp, row.sourceText].filter(Boolean).join(' · ')}
      </span>
    </span>
  )
}
// how long ago it was written, the exact time on hover (fits a laptop's window)
const whenCell = cell => (cell.data && cell.data.ts ? <span title={`${cell.data.whenText} · ${utcText(cell.data.ts)}`}>{agoText(cell.data.ts)}</span> : '')
// the count of findings, and the soft-limit pill beside it when the postmortem is over it
const findingsCell = cell => {
  const row = cell.data
  if (!row) return ''
  return <span className="d-inline-flex align-items-center gap-2">{row.findingsText}{row.overSoftLimit && <Pill tone="warn" title={POSTMORTEM_SOFT_LIMIT_NOTE}>{row.flagsText}</Pill>}</span>
}

// The tab's filters in the address; it opens on Needs processing, any time.
const FILTERS = { organization: ORGANIZATION_SPEC, source: choiceSpec(SOURCE_FILTERS), client: CLIENT_SPEC, consumer: CONSUMER_SPEC, status: STATUS_SPEC, login: LOGIN_SPEC, ...RECEIVED_SPEC }

export const PostmortemsTab = ({ consumers, onMarked, onNeedConsumers }) => {
  const ids = { org: useId(), source: useId(), client: useId() }
  const clientApps = useClientApps()
  const { filters: f, update } = useListFilters(FILTERS)
  const { organization, source, client, consumer, status, login } = f

  const received = useMemo(() => receivedWindow(f), [f])
  const { from, to, problem } = received

  const filters = useMemo(() => ({ ...listFilters({ organization, source, consumer, status, login, client }), from, to }), [organization, source, consumer, status, login, client, from, to])
  const list = useMarkableList({ fetchPage: listPostmortems, toRow: postmortemRow, filters, failedText: FAILED, kind: 'postmortem', labelOf: markLabel, consumers, onMarked, onNeedConsumers, blocked: problem, recycleTab: 'postmortems' })
  const set = key => list.change(value => update({ [key]: value }))

  const controls = (
    <>
      <OrganizationFilter id={ids.org} value={organization} onChange={set('organization')} />
      <FilterSelect id={ids.source} label="Source" value={source} onChange={set('source')} options={SOURCE_FILTERS} />
      <ClientAppFilter id={ids.client} value={client} onChange={set('client')} apps={clientApps} />
      <ConsumerStatusFilters consumers={consumers} consumer={consumer} status={status} onConsumer={set('consumer')} onStatus={set('status')} />
      <ReceivedFilter value={f} onChange={list.change(update)} />
      <PersonChip login={login} person={list.personOf(login)} onClear={() => set('login')('')} />
    </>
  )
  const actions = useMatchingActions({ list, kind: 'postmortem', filters, fromDay: received.fromDay, toDay: received.toDay })

  return (
    <MarkableList list={list} queryKey={QUERY_KEY} actions={actions} title="Postmortems" controls={controls} rowTarget={pageOf}
      emptyState={{ Icon: IconAiDocument, title: 'No postmortems', message: 'No postmortem matches these filters.' }}>
      <ColumnFormat field="titleText" header="Postmortem" sortable={false} flex={1.6} textMax={240} stacked render={titleCell} />
      <ColumnFormat field="personText" header="Person" sortable={false} textMax={200} stacked render={personOrgCell} />
      <ColumnFormat field="whenText" header="Written" sortable={false} fit="content" render={whenCell} />
      <ColumnFormat field="findingsText" header="Findings" sortable={false} fit="content" render={findingsCell} />
      <ColumnFormat field="marksText" header="Marks" sortable={false} flex={1.2} render={marksCell} />
    </MarkableList>
  )
}
